import http from '@/lib/http'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

export type SubscriptionMedication = {
  itemHash: string
  name: string | null
  status: string | null
  productName: string | null
  categories: string[]
}

export type SubscriptionOrder = {
  hash: string
  orderNumber: string | null
  type: string
  evaroType: string | null
  status: string
  createdAt: string
  totalAmount: string | null
}

export type SubscriptionListItem = {
  hash: string
  subscriptionNumber: string | null
  status: string
  evaroType: string | null
  planName: string | null
  category: string | null
  frequency: string | null
  billingPeriod: string | null
  billingInterval: string | null
  medicationCount: number
  startedAt: string
}

export type Subscription = SubscriptionListItem & {
  updatedAt: string
  cancellationReason: string | null
  totalAmount: string | null
  recurringAmount: string | null
  paymentMethod: string | null
  paymentMethodDetail: string | null
  nextPaymentDate: string | null
  cardBrand: string | null
  cardLast4: string | null
  user: {
    hash: string | null
    email: string | null
    displayName: string | null
    phone: string | null
  }
  patient: {
    hash: string
    firstName: string | null
    lastName: string | null
    dateOfBirth: string | null
  } | null
  medications: SubscriptionMedication[]
  orders: SubscriptionOrder[]
  ordersCount: number
}

type SubscriptionsResponse = {
  subscriptions: SubscriptionListItem[]
  count: number
}

async function fetchSubscriptionsByUserHash(userHash: string): Promise<SubscriptionListItem[]> {
  const { subscriptions } = (await http.get('/subscriptions', { params: { userHash } })) as SubscriptionsResponse

  return subscriptions
}

async function fetchSubscriptionByHash(hash: string): Promise<Subscription> {
  return (await http.get(`/subscriptions/${hash}`)) as Subscription
}

export function useSubscriptionsByUserHashQuery(userHash: string | undefined) {
  return useQuery({
    queryKey: ['subscriptions', 'user', userHash],
    queryFn: () => fetchSubscriptionsByUserHash(userHash as string),
    enabled: !!userHash
  })
}

export function useSubscriptionByHashQuery(hash: string) {
  return useQuery({
    queryKey: ['subscription', hash],
    queryFn: () => fetchSubscriptionByHash(hash),
    enabled: !!hash
  })
}

export type CancelSubscriptionPayload = {
  reason: string
  cancelAtPeriodEnd: boolean
}

export function useCancelSubscriptionMutation(hash: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CancelSubscriptionPayload) =>
      http.post(`/subscriptions/${hash}/cancel`, payload) as Promise<void>,
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', hash] })
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      enqueueSnackbar(
        payload.cancelAtPeriodEnd ? 'Subscription will end at the current period' : 'Subscription cancelled',
        { variant: 'success' }
      )
    }
  })
}
